import { useState, type MouseEvent, type ReactNode } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { AutoAwesome, CheckCircle, ErrorOutlined } from "@mui/icons-material";
import { useGemma, useGemmaSupported } from "../ai/gemma";
import type { FileLoadProgress, GemmaStatus } from "../ai/gemma";

const minProgress = (files: FileLoadProgress[]): number =>
  files.length === 0 ? 0 : Math.min(...files.map((f) => f.progress));

const shortLabel = (status: GemmaStatus): string => {
  switch (status.kind) {
    case "unloaded":
      return "AI off";
    case "loading":
      return `${Math.round(minProgress(status.files))}%`;
    case "ready":
      return "AI ready";
    case "busy":
      return "AI working…";
    case "error":
      return "AI unavailable";
  }
};

const FileRow = ({
  file,
  failed,
}: {
  file: FileLoadProgress;
  failed?: boolean;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ alignItems: "center", width: "100%" }}
  >
    <Typography
      variant="caption"
      noWrap
      sx={{ flexGrow: 1, fontFamily: "monospace" }}
    >
      {file.file}
    </Typography>
    {file.state === "done" ? (
      <CheckCircle sx={{ fontSize: 14, color: "success.light" }} />
    ) : failed ? (
      <ErrorOutlined sx={{ fontSize: 14, color: "error.light" }} />
    ) : (
      <Box sx={{ width: 80 }}>
        <LinearProgress
          variant="determinate"
          value={file.progress}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    )}
    <Typography
      variant="caption"
      sx={{ width: 36, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
    >
      {file.state === "done"
        ? "done"
        : failed
          ? "fail"
          : `${Math.round(file.progress)}%`}
    </Typography>
  </Stack>
);

const tooltipContent = (status: GemmaStatus): ReactNode => {
  switch (status.kind) {
    case "unloaded":
      return "Gemma 4 not loaded";
    case "ready":
      return "Gemma 4 loaded";
    case "busy":
      return "Gemma 4 generating a response";
    case "loading": {
      if (status.files.length === 0) return "Loading Gemma 4…";
      return (
        <Box sx={{ minWidth: 240, py: 0.5 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            Loading Gemma 4 — {status.files.length} file
            {status.files.length === 1 ? "" : "s"}
          </Typography>
          <Stack spacing={0.5}>
            {status.files.map((f) => (
              <FileRow key={f.file} file={f} />
            ))}
          </Stack>
        </Box>
      );
    }
    case "error": {
      if (!status.files || status.files.length === 0) return status.message;
      return (
        <Box sx={{ minWidth: 240, py: 0.5 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
            {status.message}
          </Typography>
          <Stack spacing={0.5}>
            {status.files.map((f) => (
              <FileRow key={f.file} file={f} failed={f.state !== "done"} />
            ))}
          </Stack>
        </Box>
      );
    }
  }
};

export const GemmaStatusChip = () => {
  const supported = useGemmaSupported();
  const { status, load, unload } = useGemma();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!supported) return null;

  const open = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const close = () => setAnchorEl(null);

  const handleLoad = () => {
    close();
    void load().catch(() => undefined);
  };
  const handleUnload = () => {
    close();
    unload();
  };

  const icon = (() => {
    if (status.kind === "loading" || status.kind === "busy") {
      return <CircularProgress size={14} sx={{ color: "inherit" }} />;
    }
    if (status.kind === "error") return <ErrorOutlined />;
    return <AutoAwesome />;
  })();

  const color: "default" | "success" | "warning" | "error" =
    status.kind === "ready"
      ? "success"
      : status.kind === "error"
        ? "error"
        : status.kind === "busy" || status.kind === "loading"
          ? "warning"
          : "default";

  return (
    <Box sx={{ mr: 1 }}>
      <Tooltip title={tooltipContent(status)}>
        <Chip
          icon={icon}
          label={shortLabel(status)}
          color={color}
          variant={status.kind === "unloaded" ? "outlined" : "filled"}
          onClick={open}
          size="small"
          sx={{
            color: "inherit",
            borderColor: "rgba(255,255,255,0.5)",
            minHeight: 32,
            px: 0.5,
            "& .MuiChip-icon": {
              color: "inherit",
            },
          }}
          aria-label="Gemma model status"
        />
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        {(status.kind === "unloaded" || status.kind === "error") && (
          <MenuItem onClick={handleLoad}>Load Gemma 4</MenuItem>
        )}
        {(status.kind === "ready" ||
          status.kind === "busy" ||
          status.kind === "loading") && (
          <MenuItem onClick={handleUnload}>Unload model</MenuItem>
        )}
      </Menu>
    </Box>
  );
};
